/**
 * Which deployment is this, and may a visitor trust it with a real deed?
 *
 * Derived from the Firebase project id rather than a separate VITE_ENVIRONMENT
 * flag, for one reason: a flag can be forgotten in a deploy config and the
 * banner then silently stops appearing on the one environment that needed it.
 * The project id is already required at module load (env.ts), is per-environment
 * by construction (infra/README §2 — staging and production are separate GCP
 * projects), and cannot be omitted without the app failing to start.
 *
 * FAIL-CLOSED. An unrecognised project id is treated as NOT production, so a new
 * environment shows the notice until it is deliberately added to
 * PRODUCTION_PROJECT_IDS. The inverse default would mean a typo in a project id
 * silently presents a demonstration environment as the real service — the
 * expensive direction of the error. This is the same allowlist-not-denylist
 * discipline scripts/seed-staging.ts uses to refuse production.
 *
 * There is no production project yet, so today every deployment shows the
 * notice. That is correct, not a placeholder.
 */
import { FIREBASE_CONFIG } from "./env.js";

/** Add the production project id here when it exists — and only then. */
const PRODUCTION_PROJECT_IDS: readonly string[] = [];

export const IS_PRODUCTION_DEPLOYMENT: boolean = PRODUCTION_PROJECT_IDS.includes(
  FIREBASE_CONFIG.projectId,
);

/**
 * What the notice says. Deliberately concrete about the two things a visitor
 * would otherwise have to guess: nobody is acting on what they submit, and their
 * documents are not on a deletion clock — which is true, because no retention
 * sweep exists (apps/api/src has no retention job; see db/schema/documents.ts).
 *
 * It does NOT say "your data is safe", "we will delete this later", or anything
 * else the system cannot honour. An overstated reassurance on a demonstration
 * environment is the same defect copy-honesty.test.tsx exists to catch.
 */
export const DEMO_NOTICE =
  "Demonstration environment — not a live service. No attorney receives what you " +
  "submit here, and documents uploaded to it are not covered by a deletion schedule. " +
  "Please do not upload a real deed or identity document.";
